import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { Keyboard } from '@capacitor/keyboard';

// Minimal, safe native polish for iOS/Android without changing app logic
const INTERNAL_HOSTS = new Set([
  'brrgr.com',
  'www.brrgr.com',
  '34ace5c1-f084-4291-8915-6dc56f9180c5.lovableproject.com', // Keep for compatibility
  'lovableproject.com',
]);
const INTERNAL_SUFFIXES = ['.lovableproject.com', '.brrgr.com'];
function isInternal(url: URL) {
  try {
    const host = url.hostname;
    return (
      url.origin === window.location.origin ||
      INTERNAL_HOSTS.has(host) ||
      INTERNAL_SUFFIXES.some((suf) => host.endsWith(suf))
    );
  } catch {
    return false;
  }
}
export function setupNativeExperience() {
  // 1) Intercept window.open to avoid punting to Safari unintentionally
  const originalOpen = window.open;
  (window as any).open = (url?: string | URL, target?: string | undefined, features?: string | undefined) => {
    if (!url) return null;
    try {
      const href = typeof url === 'string' ? url : url.toString();
      // Handle tel/mailto/maps and other schemes directly
      if (!href.startsWith('http')) {
        window.location.href = href;
        return null;
      }
      const dest = new URL(href, window.location.href);
      if (isInternal(dest)) {
        // Keep in-app for allowed/internal links
        window.location.assign(dest.pathname + dest.search + dest.hash);
        return null;
      }
      // External links → open in in-app Browser (SFSafariView on iOS)
      if (Capacitor.isNativePlatform()) {
        Browser.open({ url: dest.toString(), presentationStyle: 'popover' }).catch(() => {
          // Fallback
          originalOpen.call(window, dest.toString(), '_blank', 'noopener');
        });
        return null;
      }
      return originalOpen.call(window, dest.toString(), '_blank', 'noopener');
    } catch {
      // Fallback behavior
      return originalOpen.call(window, typeof url === 'string' ? url : url.toString(), target, features);
    }
  };

  // 2) Capture clicks on <a target="_blank"> to keep same-origin links in-app
  document.addEventListener(
    'click',
    (e) => {
      const el = e.target as HTMLElement | null;
      const anchor = el?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href) return;

      const isModified = (e as MouseEvent).metaKey || (e as MouseEvent).ctrlKey || (e as MouseEvent).shiftKey || (e as MouseEvent).altKey;
      if (isModified) return; // respect user intent

      const isBlank = anchor.target === '_blank';
      if (!isBlank) return;

      e.preventDefault();
      try {
        const url = new URL(href, window.location.href);
        if (isInternal(url)) {
          // Internal link: keep inside the WKWebView
          window.location.assign(url.pathname + url.search + url.hash);
        } else {
          // External link: open in in-app Browser
          if (Capacitor.isNativePlatform()) {
            Browser.open({ url: url.toString(), presentationStyle: 'popover' }).catch(() => {
              window.open(url.toString(), '_blank', 'noopener');
            });
          } else {
            window.open(url.toString(), '_blank', 'noopener');
          }
        }
      } catch {
        // If URL parsing fails, fallback to default behavior
        window.open(href, '_blank', 'noopener');
      }
    },
    true
  );

  // 3) Keyboard polish (iOS): use body resize and match theme
  try {
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    Keyboard.setResizeMode({ mode: 'native' as any }).catch(() => {});
    Keyboard.setStyle({ style: prefersDark ? 'dark' : 'light' } as any).catch(() => {});
  } catch { }
}
